import { ReactNode, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import api from '../api'
import { Project } from '../types'
import Button from '../ui/Button'
import Modal, { ModalStateProps } from '../ui/Modal'
import { SingleSelect } from '../ui/form/SingleSelect'
import { useResolver } from '../hooks'

interface ProjectSelectModalProps extends ModalStateProps {
    title?: ReactNode
    description?: ReactNode
    submitLabel?: ReactNode
    excludeIds?: number[]
    onSelect: (project: Project) => void | Promise<void>
}

export const ProjectSelectModal = ({
    open,
    onClose,
    onSelect,
    title,
    description,
    submitLabel,
    excludeIds = [],
}: ProjectSelectModalProps) => {
    const { t } = useTranslation()
    const [projects] = useResolver(useCallback(async () => await api.projects.all(), []))
    const [selected, setSelected] = useState<Project | undefined>()
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!open) setSelected(undefined)
    }, [open])

    const options = (projects ?? []).filter(p => !excludeIds.includes(p.id))

    const handleSubmit = async () => {
        if (!selected) return
        setSaving(true)
        try {
            await onSelect(selected)
        } finally {
            setSaving(false)
        }
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={title ?? t('select_project')}
            description={description}
            actions={
                <>
                    <Button variant="secondary" onClick={() => onClose(false)}>{t('cancel')}</Button>
                    <Button onClick={handleSubmit} isLoading={saving} disabled={!selected}>
                        {submitLabel ?? t('copy')}
                    </Button>
                </>
            }
        >
            <SingleSelect
                label={t('project')}
                placeholder={t('select_project')}
                options={options}
                value={selected}
                onChange={setSelected}
                searchable
            />
        </Modal>
    )
}
